import { useEffect } from "react";
import { channels } from "@/mocks/site";
import type { ArticleItem, NewsItem } from "@/mocks/types";

interface ArticleModalProps {
  item: ArticleItem | null;
  onClose: () => void;
  related: NewsItem[];
  onOpenRelated: (item: NewsItem) => void;
}

export default function ArticleModal({ item, onClose, related, onOpenRelated }: ArticleModalProps) {
  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [item, onClose]);

  if (!item) return null;

  const isNews = "channel" in item;
  const ch = isNews ? channels.find((c) => c.key === (item as NewsItem).channel) : null;

  return (
    <div
      className="animate-fader fixed inset-0 z-[80] flex items-center justify-center bg-foreground-950/70 p-3 backdrop-blur-sm md:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="瓜讯详情"
    >
      <div
        className="animate-popin flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-background-50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* head */}
        <div className="flex items-center justify-between gap-3 border-b border-background-200 bg-background-100/60 px-5 py-3.5 md:px-7">
          <div className="flex min-w-0 items-center gap-2">
            {ch ? (
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-100 px-2 py-1 text-xs font-semibold text-primary-800">
                <i className={ch.icon} aria-hidden="true"></i>
                {ch.name}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-accent-100 px-2 py-1 text-xs font-semibold text-accent-800">
                <i className="ri-book-open-line" aria-hidden="true"></i>
                深度档案
              </span>
            )}
            <span className="truncate text-xs text-foreground-400">
              {isNews ? `瓜源：${(item as NewsItem).source}` : `整理 · ${item.date}`}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background-200/70 text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
            aria-label="关闭"
          >
            <i className="ri-close-line text-lg" aria-hidden="true"></i>
          </button>
        </div>

        {/* scroll body */}
        <div className="overflow-y-auto px-5 py-6 md:px-9 md:py-8">
          <h3 className="font-heading text-xl font-black leading-snug text-foreground-900 md:text-2xl">
            {item.title}
          </h3>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {item.tags.map((t) => (
              <span key={t} className="rounded-full bg-background-200 px-2.5 py-1 text-xs font-semibold text-foreground-600">
                #{t}
              </span>
            ))}
            {isNews && (
              <span className="ml-auto inline-flex items-center gap-1 whitespace-nowrap text-xs font-bold text-accent-700">
                <i className="ri-fire-fill" aria-hidden="true"></i>
                {(item as NewsItem).heat >= 1000
                  ? `${((item as NewsItem).heat / 1000).toFixed(1)}k 热度`
                  : `${item.heat} 热度`}
              </span>
            )}
          </div>

          <div className="mt-6 space-y-4">
            {item.body.map((p, i) => (
              <p key={i} className="text-[15px] leading-[1.9] text-foreground-700">
                {i === 0 && (
                  <span className="float-left mr-2 mt-1 select-none font-heading text-[46px] font-black leading-[0.8] text-primary-500">
                    瓜
                  </span>
                )}
                {p}
              </p>
            ))}
          </div>

          <p className="mt-6 rounded-xl border border-accent-200/70 bg-accent-100/50 px-4 py-3 text-[13px] leading-relaxed text-foreground-600">
            <i className="mr-1.5 ri-information-line text-accent-700" aria-hidden="true"></i>
            瓜田雷达提示：本条内容未经当事人或官方确认，属于<strong>“尚未官宣”</strong>的圈内情报，
            请理性看待、谨慎传播，一切以艺人及官方最终发布为准。
          </p>
        </div>

        {/* related */}
        {related.length > 0 && (
          <div className="border-t border-background-200 px-5 py-4 md:px-9">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-foreground-400">继续吃瓜</p>
            <div className="mt-3 space-y-1">
              {related.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onOpenRelated(r)}
                  className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-background-100"
                >
                  <i className="mt-0.5 text-primary-500 ri-corner-down-right-line" aria-hidden="true"></i>
                  <span className="text-sm leading-snug text-foreground-700 hover:text-primary-700 line-clamp-1">{r.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}